import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ClipboardList, Search, Filter, Calendar, UserCog, Briefcase, Building2, UserCheck } from 'lucide-react';
import api from '@/utils/api';

const ExecutiveActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalLogs, setTotalLogs] = useState(0);
  const [executives, setExecutives] = useState([]);
  
  // Filters
  const [executiveFilter, setExecutiveFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const limit = 50;

  useEffect(() => {
    fetchExecutives();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, executiveFilter, actionFilter, entityTypeFilter, fromDate, toDate]);

  const fetchExecutives = async () => {
    try {
      const response = await api.get('/admin/executives');
      setExecutives(response.data || []);
    } catch (error) {
      console.error('Failed to fetch executives');
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `/admin/executive-activity-logs?page=${page}&limit=${limit}`;
      if (executiveFilter !== 'all') url += `&executive_id=${executiveFilter}`;
      if (actionFilter !== 'all') url += `&action=${actionFilter}`;
      if (entityTypeFilter !== 'all') url += `&entity_type=${entityTypeFilter}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      
      const response = await api.get(url);
      setLogs(response.data.data || []);
      setTotalLogs(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setExecutiveFilter('all');
    setActionFilter('all');
    setEntityTypeFilter('all');
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const getActionBadge = (action) => {
    switch (action) {
      case 'activate':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Activate</Badge>;
      case 'deactivate':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Deactivate</Badge>;
      case 'approve':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Approve</Badge>;
      case 'reject':
        return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Reject</Badge>;
      case 'edit':
        return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Edit</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'job':
        return <Briefcase className="w-4 h-4 text-blue-500" />;
      case 'employer':
        return <Building2 className="w-4 h-4 text-purple-500" />;
      case 'job_seeker':
        return <UserCheck className="w-4 h-4 text-green-500" />;
      default:
        return <ClipboardList className="w-4 h-4 text-slate-500" />;
    }
  };

  const totalPages = Math.ceil(totalLogs / limit);

  if (loading && page === 1) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
            Executive Activity Logs
          </h1>
          <p className="text-slate-600">Audit trail of all executive actions</p>
        </div>
        <Badge variant="outline" className="px-3 py-1.5">
          <ClipboardList className="w-4 h-4 mr-1" /> {totalLogs} Logs
        </Badge>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Executive Filter */}
          <div className="min-w-[200px]">
            <label className="block text-sm font-medium text-slate-700 mb-1">Executive</label>
            <Select value={executiveFilter} onValueChange={setExecutiveFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Executives" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Executives</SelectItem>
                {executives.map(exec => (
                  <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Action Filter */}
          <div className="min-w-[150px]">
            <label className="block text-sm font-medium text-slate-700 mb-1">Action</label>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="activate">Activate</SelectItem>
                <SelectItem value="deactivate">Deactivate</SelectItem>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="reject">Reject</SelectItem>
                <SelectItem value="edit">Edit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Entity Type Filter */}
          <div className="min-w-[150px]">
            <label className="block text-sm font-medium text-slate-700 mb-1">Entity Type</label>
            <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="job">Job</SelectItem>
                <SelectItem value="employer">Employer</SelectItem>
                <SelectItem value="job_seeker">Job Seeker</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">From Date</label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">To Date</label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-40"
            />
          </div>

          <Button variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </Card>

      {/* Logs Table */}
      <Card className="border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Sl.No</TableHead>
              <TableHead>Timestamp</TableHead>
              <TableHead>Executive</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity Type</TableHead>
              <TableHead>Entity Name</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-600">No activity logs found</p>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log, index) => (
                <TableRow key={log.id}>
                  <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <UserCog className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{log.executive_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{getActionBadge(log.action)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getEntityIcon(log.entity_type)}
                      <span className="capitalize">{log.entity_type?.replace('_', ' ')}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{log.entity_name}</TableCell>
                  <TableCell>
                    <div className="text-xs text-slate-500">
                      {log.details?.company_name && (
                        <span>Company: {log.details.company_name}</span>
                      )}
                      {log.details?.mobile && (
                        <span>Mobile: {log.details.mobile}</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-600">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalLogs)} of {totalLogs} logs
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutiveActivityLogs;
